import { Box, Button, CardMedia, Dialog, Grid, IconButton, Typography, useTheme } from "@mui/material";
import { CommonLayout } from "../commonComponents";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../../redux-store/reducers";
import { getApplicationDetails, getDocuments, isFormValidForSubmission, nonIndividualFormValidForSubmission, updateApplication } from "../../redux-store/actions/application";
import { ApplicationProps, nationaliyType, RiskProfileMaster, Document } from "../../redux-store/types/api-types";
import { getNationalityList } from "../../redux-store/actions/userManagement";
import { riskProfileMasterData } from "../../redux-store/actions";
import { showError } from "../../redux-store/actions/auth";
import * as yup from 'yup';
import { AMC_APPROVER_CHECK_FOR_INDIVIDUAL, APPLICATION_TYPE, USER_ROLES } from "../../utils/constant";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { NonIndividualApplicationLayout } from "../NonIndividualApplicationDetails";
import { ApplicationDetailsCommonLayout, RejectApproveButtons } from "../ApplicationDetails";
import CloseIcon from '@mui/icons-material/Close';
import { Formik } from "formik";
import { MFTextField } from "../../lib/formik";
import { checkIfApplicationIsNonIndividual, sendApplication_Nri } from "../../utils/utilityFunctions";

const initialCommentValues: Partial<ApplicationProps> = { comment: '' };

const Commentvalidation = yup.object().shape({
    comment: yup.string().required('Please provide Comment'),
});

const initialValues: Partial<ApplicationProps> = { rejectedReason: '' };

const resonValidation = yup.object().shape({
    rejectedReason: yup.string().required('Please enter the reason for rejection'),
});

export default function ComplianceApplicationDetails(): JSX.Element {
    const theme = useTheme()

    const history = useHistory();
    const dispatch = useDispatch();

    const applicationDetailRef = useRef<HTMLDivElement>();

    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);
    const [openSendBackDialog, setOpenSendBackDialog] = useState(false);
    const [sendBackLoading, setSendBackLoading] = useState(false);

    const { role = '' } = useSelector((store: RootStateType) => store.auth);
    const { company = "WMS" } = useSelector((store: RootStateType) => store.navbarInfo)
    const { currencyCode = "INR", locale = "en-IN" } = useSelector((store: RootStateType) => store.currencyInfo);
    const { application } = useSelector((store: RootStateType) => store.application);
    const { status, applicationType } = application || {};

    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        (async function () {
            try {
                (await dispatch(
                    getApplicationDetails(id)
                )) as unknown as ApplicationProps;
            } catch (e) {
                console.log(e);
            } finally {
                setLoading(false);
            }
        })();
        setLoading(true);
    }, []);

    const validateForm = async () => {
        const nationalitiesMdmsMasters = (await dispatch(
            getNationalityList()
        )) as unknown as nationaliyType;
        const documentsResponse = (await dispatch(getDocuments())) as unknown as Document;
        const responseRiskProfile = (await dispatch(
            riskProfileMasterData({
                riskProfileGroup: application?.riskProfileGroup ?? null,
                company: application?.company ?? null
            })
        )) as unknown as RiskProfileMaster;
        if (application?.applicationType?.toLowerCase() === "individual") {
            await isFormValidForSubmission(
                application as ApplicationProps,
                true,
                true,
                nationalitiesMdmsMasters,
                documentsResponse,
                role,
                responseRiskProfile.riskProfile,
                currencyCode,
                company
            );
        }
        if (application?.applicationType?.toLowerCase() === "non_individual") {
            const { applicants = [] } = application || {};
            const getInvestorType = applicants[0]?.investorType?.toString();

            if (getInvestorType && Object.keys(documentsResponse).includes(getInvestorType)) {
                await nonIndividualFormValidForSubmission(
                    application,
                    documentsResponse[getInvestorType] ?? [],
                    nationalitiesMdmsMasters.countries,
                    undefined,
                    currencyCode,
                    locale,
                    true
                );
            }
        }
    };

    const handleOpen = () => setOpen(true);

    const handleDialogOpen = async () => {
        try {
            setApproveLoading(true);
            await validateForm();
            setApproveLoading(false);
            setOpenDialog(true);
        } catch (e) {
            setApproveLoading(false);
            typeof e === 'string' && dispatch(showError(e));
            console.error((e as Error).message);
        }
    };

    const handleSendBack = () => setOpenSendBackDialog(true);

    const handleSendBackClose = () => {
        setOpenSendBackDialog(false), setSendBackLoading(false);
    };
    const handleClose = () => {
        setOpen(false), setApproveLoading(false);
    };
    const handleDialogClose = () => {
        setOpenDialog(false), setRejectLoading(false);
    };

    const onSubmit = async (values: Partial<ApplicationProps>) => {
        try {
            handleClose();
            setRejectLoading(true);
            if (application) {
                const response = (await dispatch(
                    updateApplication({
                        body: {
                            ...application,
                            rejectedReason: values.rejectedReason,
                            status: 'rejected',
                        },
                        applicationId: application.id,
                    })
                )) as unknown as ApplicationProps;

                if (response) {
                    history.push('/compliance-approval');
                }
            }
        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setRejectLoading(false);
        }
    };
    const onSendBackSubmit = async (values: Partial<ApplicationProps>) => {
        try {
            handleSendBackClose();
            setSendBackLoading(true);
            if (application) {
                const response = (await dispatch(
                    updateApplication({
                        body: {
                            ...application,
                            comment: values.comment,
                            status: 'draft',
                            applicationSentBack: true,
                        },
                        applicationId: application.id,
                    })
                )) as unknown as ApplicationProps;

                if (response) {
                    history.push('/compliance-approval');
                }
            }
        } catch (e) {
            console.error((e as Error).message);
        } finally {
            setSendBackLoading(false);
        }
    };
    return (
        <CommonLayout>
            <>
                <Box sx={{ px: { xs: 2, sm: 5 }, mt: { xs: 2, sm: 3 } }} ref={applicationDetailRef}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            justifyContent: 'space-between',
                            // mt: 5,
                        }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                rowGap: "15px"
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography sx={{ color: 'text.primary' }} variant='h1'>Applications Details</Typography>
                            </Box>
                            {!loading && (
                                <Typography
                                    variant='body_600'
                                    sx={{ color: theme.palette.text.primary }}
                                >
                                    {application?.onboardingType?.toLowerCase() === "minor" ?
                                        `Minor Investor ${application?.accountType ? '(' + application?.accountType + ')' : ''}` :
                                        ""
                                    }
                                    {(application?.onboardingType?.toLowerCase() === "normal" &&
                                        application?.applicationType === APPLICATION_TYPE.INDIVIDUAL) ?
                                        `Individual Investor ${application?.accountType ?
                                            '(' + application?.accountType + ')' : ''}` :
                                        ""
                                    }
                                    {(application?.onboardingType?.toLowerCase() === "normal" &&
                                        application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL) ?
                                        `Non Individual Investor ${application?.accountType ?
                                            '(' + application?.accountType + ')' : ''}` :
                                        ""
                                    }
                                </Typography>
                            )}
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end'
                            }}
                        >
                            <Typography variant="body_600">
                                {`Application ID: ${application?.applicationNumber ?? "-"}`}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        sx={{
                            position: 'fixed',
                            right: 0,
                            bottom: 75,
                            borderRadius: '5px  0 0 5px',
                            '&,:hover': {
                                bgcolor: 'primary.main',
                            },
                        }}
                        onClick={() => applicationDetailRef.current?.scrollIntoView({ behavior: 'smooth' })}>
                        <ArrowUpwardIcon fontSize="large" sx={{ color: 'common.white' }} />
                    </IconButton>
                    {applicationType === APPLICATION_TYPE.NON_INDIVIDUAL ? (
                        <NonIndividualApplicationLayout
                            loading={loading}
                            application={application ?? {} as ApplicationProps}
                        />
                    ) : (
                        <ApplicationDetailsCommonLayout
                            loading={loading}
                            application={application ?? {} as ApplicationProps}
                        />
                    )}
                    {(
                        application &&
                        application?.status === 'sent_to_compliance'
                    ) && <RejectApproveButtons
                            loading={loading}
                            handleOpen={handleOpen}
                            handleDialogOpen={handleDialogOpen}
                            approve={approveLoading}
                            reject={rejectLoading}
                            sendBack={sendBackLoading}
                            handleSendBack={handleSendBack}
                        />}
                    <Dialog
                        onClose={handleClose}
                        open={open}
                        sx={{
                            '.MuiPaper-root ': {
                                p: 4,
                                width: { xs: '70%', sm: '70%' },
                            },
                        }}>
                        <IconButton onClick={handleClose} sx={{ alignSelf: 'flex-end' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <Box sx={{ mb: 2 }}>
                            <CardMedia
                                component="img"
                                src="/images/warning-icon-red.svg"
                                sx={{ width: 'unset', mx: 'auto' }}
                            />
                        </Box>
                        <Box
                            sx={{
                                width: { xs: '100%', sm: '80%' },
                                mx: 'auto',
                                '.MuiTypography-root': {
                                    fontSize: 18,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                },
                            }}>
                            <Typography sx={{ color: 'primary.main' }}>
                                Are you sure you want to{' '}
                                <Typography component="span" sx={{ color: '#BD2932' }}>
                                    Reject
                                </Typography>{' '}
                                the application
                            </Typography>
                            <Typography sx={{ color: '#BD2932', mt: 5 }}>
                                {' '}
                                Give reason for application rejection *
                            </Typography>
                            <Formik
                                initialValues={initialValues}
                                onSubmit={onSubmit}
                                validationSchema={resonValidation}>
                                {({ handleSubmit }) => (
                                    <Box component="form" noValidate onSubmit={handleSubmit}>
                                        <MFTextField
                                            name="rejectedReason"
                                            multiline
                                            rows={4}
                                            placeholder="Type your answer here..."
                                        />

                                        <Grid container sx={{ my: 3, justifyContent: 'center', gap: 1.5 }}>
                                            <Button
                                                onClick={() => {
                                                    handleClose();
                                                }}
                                                variant="outlined"
                                                sx={{
                                                    fontSize: 15,
                                                    color: 'primary.main',
                                                    minWidth: 180,
                                                    letterSpacing: 2,
                                                    fontWeight: 500,
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    fontSize: 15,
                                                    minWidth: 180,
                                                    letterSpacing: 2,
                                                    fontWeight: 400,
                                                }}
                                                type="submit">
                                                Continue
                                            </Button>
                                        </Grid>
                                    </Box>
                                )}
                            </Formik>
                        </Box>
                    </Dialog>
                    <Dialog
                        onClose={handleDialogClose}
                        open={openDialog}
                        sx={{
                            '.MuiPaper-root ': {
                                p: 4,
                                width: { xs: '70%', sm: '70%' },
                            },
                        }}>
                        <IconButton onClick={handleDialogClose} sx={{ alignSelf: 'flex-end' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <Box
                            sx={{
                                width: { xs: '100%', sm: '80%' },
                                mx: 'auto',
                                '.MuiTypography-root': {
                                    fontSize: 20,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                },
                            }}>
                            <Typography sx={{ color: 'primary.main' }}>
                                Are you sure you want to{' '}
                                <Typography component="span" sx={{ color: 'green' }}>
                                    Approve
                                </Typography>{' '}
                                the application
                            </Typography>
                        </Box>
                        <Grid container sx={{ my: 3, justifyContent: 'center', gap: 1.5 }}>
                            <Button
                                onClick={() => {
                                    handleDialogClose();
                                }}
                                variant="outlined"
                                sx={{
                                    fontSize: 15,
                                    color: 'primary.main',
                                    minWidth: 180,
                                    letterSpacing: 2,
                                    fontWeight: 500,
                                }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    fontSize: 15,
                                    minWidth: 180,
                                    letterSpacing: 2,
                                    fontWeight: 400,
                                }}
                                onClick={async () => {
                                    try {
                                        handleDialogClose();
                                        setApproveLoading(true);
                                        const response = (await dispatch(
                                            updateApplication({
                                                body: {
                                                    ...application,
                                                    status: 'sent_to_amc_approver',
                                                },
                                                applicationId: application?.id ?? "",
                                            })
                                        )) as unknown as ApplicationProps;

                                        if (response) {
                                            history.push('/compliance-approval');
                                        }
                                    } catch (e) {
                                        console.error((e as Error).message);
                                    } finally {
                                        setApproveLoading(false);
                                    }
                                }}>
                                Continue
                            </Button>
                        </Grid>
                    </Dialog>
                    <Dialog
                        onClose={handleSendBackClose}
                        open={openSendBackDialog}
                        sx={{
                            '.MuiPaper-root ': {
                                p: 4,
                                width: { xs: '70%', sm: '70%' },
                            },
                        }}>
                        <IconButton onClick={handleSendBackClose} sx={{ alignSelf: 'flex-end' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        {/* <Box sx={{ mb: 2 }}>
              <CardMedia
                component="img"
                src="/images/warning-icon-red.svg"
                sx={{ width: 'unset', mx: 'auto' }}
              />
            </Box> */}
                        <Box
                            sx={{
                                width: { xs: '100%', sm: '80%' },
                                mx: 'auto',
                                '.MuiTypography-root': {
                                    fontSize: 18,
                                    fontWeight: 500,
                                    textAlign: 'justify',
                                },
                            }}>
                            <Typography sx={{ color: 'primary.main' }}>
                                Are you sure you want to{' '}
                                <Typography component="span" sx={{ color: '#BD2932' }}>
                                    Send Back
                                </Typography>{' '}
                                the application
                            </Typography>
                            <Typography sx={{ color: '#BD2932', mt: 5 }}> Provide Comments *</Typography>
                            <Formik
                                initialValues={initialCommentValues}
                                onSubmit={onSendBackSubmit}
                                validationSchema={Commentvalidation}>
                                {({ handleSubmit }) => (
                                    <Box component="form" noValidate onSubmit={handleSubmit}>
                                        <MFTextField
                                            name="comment"
                                            multiline
                                            rows={4}
                                            placeholder="Type your answer here..."
                                        />

                                        <Grid container sx={{ my: 3, justifyContent: 'center', gap: 1.5 }}>
                                            <Button
                                                onClick={handleSendBackClose}
                                                variant="outlined"
                                                sx={{
                                                    fontSize: 15,
                                                    color: 'primary.main',
                                                    minWidth: 180,
                                                    letterSpacing: 2,
                                                    fontWeight: 500,
                                                }}>
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="contained"
                                                sx={{
                                                    fontSize: 15,
                                                    minWidth: 180,
                                                    letterSpacing: 2,
                                                    fontWeight: 400,
                                                }}
                                                type="submit">
                                                Continue
                                            </Button>
                                        </Grid>
                                    </Box>
                                )}
                            </Formik>
                        </Box>
                    </Dialog>
                </Box>
            </>
        </CommonLayout>
    );
};